import { IPagination, IPaginationRequest } from "interfaces/pagination";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import AvatarLetter from "components/common/AvatarLetter";
import { Button } from "@mui/material";
import Header from "components/common/Header";
import { IReward } from "interfaces/reward";
import RewardServices from "services/reward";
import Swal from "sweetalert2";
import Table from "components/common/Table";
import Typography from "@mui/material/Typography";
import useAlert from "hooks/useAlert";
import useToken from "hooks/useToken";
import withAuth from "hocs/withAuth";
import withPermission from "hocs/withPermission";

interface IRewardData {
  id: number;
  spend: number;
  cashback: number;
  image: string;
  image_disable: string;
  last_edited_at: string;
  created_by: string;
  edited_by: string;
}

const ListReward = () => {
  let line_oa_code = "ONLY_GOLF";
  const navigate = useNavigate();
  const { token } = useToken();
  const { onAlert } = useAlert();
  const [data, setData] = useState<{
    data: IRewardData[];
    pagination: IPagination;
  }>({
    data: [],
    pagination: {
      page: 1,
      total_item: 0,
      total_page: 0,
    },
  });
  const [pagination, setPagination] = useState<IPaginationRequest>({
    page: 1,
    per_page: 10,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = useCallback(() => {
    RewardServices.getAll(token as string, line_oa_code as string, {
      per_page: pagination.per_page,
      page: pagination.page,
    })
      .then((res) => {
        let paginationRes: IPagination = res.data.pagination;
        setData({
          data: res.data.data.map((d: IReward) => {
            return {
              id: d.id,
              spend: d.spend,
              cashback: d.cashback,
              image: d.image,
              image_disable: d.image_disable,
              last_edited_at: d.last_edited_at,
              created_by: d.admin_created.name,
              edited_by: d.admin_edited.name,
            };
          }),
          pagination: {
            page: paginationRes.page,
            total_item: paginationRes.total_item,
            total_page: paginationRes.total_page,
          },
        });
        setIsLoading(false);
      })
      .catch((err) => {
        onAlert("error", err.response.data.message);
      });
  }, [pagination, line_oa_code, onAlert, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDelete = (item: IReward) => {
    Swal.fire({
      icon: "warning",
      html: `ต้องการลบรางวัลนี้ ใช่ หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        RewardServices.delete(token as string, line_oa_code as string, item.id)
          .then((res) => {
            fetchData();
            onAlert("success", "ลบ Reward สำเร็จ");
          })
          .catch((err) => {
            onAlert("error", err.response.data.message);
          });
      }
    });
  };

  return (
    <>
      <Header
        title="Reward"
        breadcrumbs={[
          {
            label: "Dashboard",
            href: `/dashboard`,
          },
          {
            label: "Only Golf",
            href: `/dashboard/only-golf`,
          },
          {
            label: "Reward",
            href: "/reward/only-golf/list",
          },
          {
            label: "List",
          },
        ]}
        actions={[
          <Link to={`/reward/only-golf/create`}>
            <Button variant="contained" size="large">
              <AddIcon
                sx={{
                  fontSize: 14,
                  mr: 1,
                }}
              />
              <Typography>Create</Typography>
            </Button>
          </Link>,
        ]}
      />
      <Table
        headers={[
          {
            key: "image",
            label: "Image",
            render: (value: string, index: number, item: IRewardData) => (
              <img
                style={{
                  borderRadius: "8px",
                  width: "80px",
                  height: "80px",
                  maxHeight: "80px",
                  objectFit: "cover",
                }}
                src={process.env.REACT_APP_FILE + value}
                alt={item.cashback + " cashback"}
              />
            ),
          },
          {
            key: "image_disable",
            label: "Image Disable",
            render: (value: string, index: number, item: IRewardData) => (
              <img
                style={{
                  borderRadius: "8px",
                  width: "80px",
                  height: "80px",
                  maxHeight: "80px",
                  objectFit: "cover",
                }}
                src={process.env.REACT_APP_FILE + value}
                alt={item.cashback + " cashback"}
              />
            ),
          },
          {
            key: "spend",
            label: "Spending",
            render: (value: string) => (
              <Typography sx={{ ml: 2 }}>{value.toLocaleString()}</Typography>
            ),
          },
          {
            key: "cashback",
            label: "Cashback",
            render: (value: string) => (
              <Typography sx={{ ml: 2 }}>{value.toLocaleString()}</Typography>
            ),
          },
          {
            key: "created_by",
            label: "Created By",
            render: (value) => <AvatarLetter label={value} tooltip />,
          },
          {
            key: "edited_by",
            label: "Edited By",
            render: (value) => <AvatarLetter label={value} tooltip />,
          },
          {
            key: "last_edited_at",
            label: "Last Edited At",
            render: (value) => (
              <Typography>{new Date(value).toLocaleString("th-TH")}</Typography>
            ),
          },
        ]}
        actions={{
          onDetail: (item: IReward) => navigate(`/reward/only-golf/${item.id}`),
          onEdit: (item: IReward) =>
            navigate(`/reward/only-golf/${item.id}/edit`),
          onDelete: (item: IReward) => onDelete(item),
        }}
        data={data.data}
        isLoading={isLoading}
        pagination={
          data.pagination
            ? {
                ...data.pagination,
                onChange: (page) =>
                  setPagination((pagination) => {
                    return {
                      ...pagination,
                      page,
                    };
                  }),
              }
            : undefined
        }
      />
    </>
  );
};

export default withAuth(withPermission(ListReward, "MANAGE_ONLY_GOLF"));
